import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(null);

// Check if it's night time (7 PM - 7 AM)
const isNightTime = () => {
    const hour = new Date().getHours();
    return hour >= 19 || hour < 7;
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider = ({ children }) => {
    // Theme mode: 'auto', 'light', 'dark'
    const getInitialMode = () => {
        const stored = localStorage.getItem('aldrin_theme_mode');
        if (stored && ['auto', 'light', 'dark'].includes(stored)) {
            return stored;
        }
        return 'auto'; // Default to auto mode
    };
    
    const [themeMode, setThemeMode] = useState(getInitialMode);
    const [isDark, setIsDark] = useState(false);
    
    // Resolve actual theme from mode
    const resolveTheme = useCallback(() => {
        if (themeMode === 'dark') return 'dark';
        if (themeMode === 'light') return 'light';
        // Auto mode - check time
        return isNightTime() ? 'dark' : 'light';
    }, [themeMode]);
    
    // Apply theme changes
    useEffect(() => {
        const actualTheme = resolveTheme();
        setIsDark(actualTheme === 'dark');
        
        const root = document.documentElement;
        
        if (actualTheme === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
        
        // Save mode preference
        localStorage.setItem('aldrin_theme_mode', themeMode);
    }, [themeMode, resolveTheme]);
    
    // Auto-update theme when time changes (check every minute)
    useEffect(() => {
        if (themeMode !== 'auto') return;
        
        const checkTime = () => {
            const actualTheme = resolveTheme();
            setIsDark(actualTheme === 'dark');
            
            const root = document.documentElement;
            if (actualTheme === 'dark') {
                root.classList.add('dark');
            } else {
                root.classList.remove('dark');
            }
        };
        
        const interval = setInterval(checkTime, 60000); // Check every minute
        return () => clearInterval(interval);
    }, [themeMode, resolveTheme]);
    
    // Cycle through modes: auto -> dark -> light -> auto
    const toggleTheme = () => {
        setThemeMode(prev => {
            if (prev === 'auto') return 'dark';
            if (prev === 'dark') return 'light';
            return 'auto';
        });
    };
    
    const setTheme = (mode) => {
        if (['auto', 'light', 'dark'].includes(mode)) {
            setThemeMode(mode);
        }
    };
    
    const value = {
        themeMode,
        theme: isDark ? 'dark' : 'light',
        isDark,
        isLight: !isDark,
        isAuto: themeMode === 'auto',
        toggleTheme,
        setTheme,
        setLightTheme: () => setTheme('light'),
        setDarkTheme: () => setTheme('dark'),
        setAutoTheme: () => setTheme('auto'),
    };
    
    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};
